import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  User,
  Sample,
  SampleUpdateInput,
  SampleTransferInput,
  Location,
  WarehouseInventory,
  StocktakeRecord,
  StocktakeDetail,
  Shelf,
  Activity,
  DashboardStats,
  BorrowStats,
  FeishuAuthorizeResponse,
  FeishuDirectoryCandidate,
  FeishuDirectorySyncState,
  FeishuLoginResponse,
  PaginatedResponse,
  SampleDropdownOption,
  SampleDropdownOptionType,
  SignedOperationSignatures,
  BorrowRequest,
  BorrowRequestPage,
  BorrowRequestStatus,
  SampleReturnInput,
  WebSignature
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

const safeMethods = new Set(['get', 'head', 'options']);

// 非安全请求自动携带双提交 CSRF Header。
apiClient.interceptors.request.use(
  (config) => {
    const method = config.method?.toLowerCase() || 'get';
    if (!safeMethods.has(method)) {
      const csrfToken = readCookie('sampleflow_csrf');
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 由 AuthContext 统一处理会话过期，避免维护第二份客户端身份状态。
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event('sampleflow:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// 认证 API
export const authAPI = {
  authorizeFeishu: () =>
    apiClient.post<FeishuAuthorizeResponse>('/api/auth/feishu/authorize'),

  callbackFeishu: (data: { code: string; state: string }) =>
    apiClient.post<FeishuLoginResponse>('/api/auth/feishu/callback', data),

  getCurrentUser: () =>
    apiClient.get<User>('/api/auth/me'),

  logout: () => apiClient.post('/api/auth/logout')
};

// 样品 API
export const samplesAPI = {
  getAll: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    asset_category?: string;
    category?: string;
    sample_stage?: string;
    search?: string;
    rfid_no?: string;
  }) =>
    apiClient.get<PaginatedResponse<Sample>>('/api/samples', { params }),

  getById: (id: number) =>
    apiClient.get<Sample>(`/api/samples/${id}`),

  getActivities: (id: number, params?: { page?: number; page_size?: number }) =>
    apiClient.get(`/api/samples/${id}/activities`, { params }),

  create: (data: {
    inbound_date?: string;
    business_unit?: string;
    customer?: string;
    season?: string;
    asset_category?: string;
    work_no?: string;
    art_no?: string;
    style_type?: string;
    sample_stage?: string;
    color_name?: string;
    specification?: string;
    inbound_quantity?: number;
    technician?: string;
    location?: string;
    rfid_no?: string;
    notes?: string;
  }) =>
    apiClient.post<Sample>('/api/samples', data),

  update: (id: number, data: SampleUpdateInput) =>
    apiClient.put<Sample>(`/api/samples/${id}`, data),

  transfer: (id: number, data: SampleTransferInput) =>
    apiClient.post<Sample>(`/api/samples/${id}/transfer`, data),

  delete: (id: number) =>
    apiClient.delete(`/api/samples/${id}`),

  borrow: (id: number, data: {
    borrower: string;
    expected_return: string;
    purpose: string;
    destination: string;
  } & SignedOperationSignatures) =>
    apiClient.post<Sample>(`/api/samples/${id}/borrow`, data),

  return: (id: number, data: SampleReturnInput) =>
    apiClient.post<Sample>(`/api/samples/${id}/return`, data),

  out: (id: number, data: {
    reason: string;
    destination: string;
  } & SignedOperationSignatures) =>
    apiClient.post<Sample>(`/api/samples/${id}/out`, data),

  findByRfid: (rfidNo: string, status: Sample['status'] = 'in_stock') =>
    apiClient.get<PaginatedResponse<Sample>>('/api/samples', {
      params: { rfid_no: rfidNo, status, page: 1, page_size: 1 }
    }),

  scrap: (id: number, data: { reason: string } & SignedOperationSignatures) =>
    apiClient.post<Sample>(`/api/samples/${id}/scrap`, data),

  destroy: (id: number, data: { method: string } & SignedOperationSignatures) =>
    apiClient.post<Sample>(`/api/samples/${id}/destroy`, data)
};

export const borrowRequestsAPI = {
  getAll: (params?: { page?: number; page_size?: number; status?: BorrowRequestStatus; mine?: boolean }) =>
    apiClient.get<BorrowRequestPage>('/api/borrow-requests', { params }),
  create: (data: { sample_id: number; expected_return: string; purpose: string; destination: string }) =>
    apiClient.post<BorrowRequest>('/api/borrow-requests', data),
  cancel: (id: number) =>
    apiClient.post<BorrowRequest>(`/api/borrow-requests/${id}/cancel`),
  reject: (id: number, data: { reason: string }) =>
    apiClient.post<BorrowRequest>(`/api/borrow-requests/${id}/reject`, data),
  fulfill: (id: number, data: { borrower_signature: WebSignature }) =>
    apiClient.post<BorrowRequest>(`/api/borrow-requests/${id}/fulfill`, data)
};

export const sampleDropdownOptionsAPI = {
  getAll: (params?: {
    option_type?: SampleDropdownOptionType;
    include_inactive?: boolean;
  }) =>
    apiClient.get<SampleDropdownOption[]>('/api/sample-dropdown-options', { params }),

  create: (data: {
    option_type: SampleDropdownOptionType;
    name: string;
  }) =>
    apiClient.post<SampleDropdownOption>('/api/sample-dropdown-options', data),

  update: (id: number, data: {
    name?: string;
    is_active?: boolean;
  }) =>
    apiClient.put<SampleDropdownOption>(`/api/sample-dropdown-options/${id}`, data),

  reorder: (data: {
    option_type: SampleDropdownOptionType;
    option_ids: number[];
  }) =>
    apiClient.put<SampleDropdownOption[]>('/api/sample-dropdown-options/reorder', data)
};

// 区域 API
export const locationsAPI = {
  getInventory: (params: {
    location_id?: number;
    shelf_id?: number;
    unassigned?: boolean;
    status?: 'in_stock' | 'borrowed' | 'scrapped';
    search?: string;
    page?: number;
    page_size?: number;
  }) => apiClient.get<WarehouseInventory>('/api/locations/inventory', { params }),

  getAll: (params?: { skip?: number; limit?: number }) =>
    apiClient.get<Location[]>('/api/locations', { params }),

  getById: (id: number) =>
    apiClient.get<Location>(`/api/locations/${id}`),

  create: (data: { name: string; description?: string }) =>
    apiClient.post<Location>('/api/locations', data),

  update: (id: number, data: { name?: string; description?: string }) =>
    apiClient.put<Location>(`/api/locations/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/api/locations/${id}`),

  getShelves: (locationId: number) =>
    apiClient.get<Shelf[]>(`/api/locations/${locationId}/shelves`),

  createShelf: (locationId: number, data: { name: string; description?: string }) =>
    apiClient.post<Shelf>(`/api/locations/${locationId}/shelves`, data),

  updateShelf: (_locationId: number, shelfId: number, data: { name?: string; description?: string }) =>
    apiClient.put<Shelf>(`/api/locations/shelves/${shelfId}`, data),

  deleteShelf: (_locationId: number, shelfId: number) =>
    apiClient.delete(`/api/locations/shelves/${shelfId}`)
};

// 用户 API
export const usersAPI = {
  getAll: (params?: { skip?: number; limit?: number; role?: string; status?: string; search?: string }) =>
    apiClient.get<User[]>('/api/users', { params }),

  getById: (id: number) =>
    apiClient.get<User>(`/api/users/${id}`),

  createFromFeishu: (data: {
    open_id: string;
    department: string;
    role: User['role'];
    email?: string;
  }) =>
    apiClient.post<User>('/api/users/from-feishu', data),

  bindFeishu: (id: number, data: {
    open_id: string;
    department: string;
    role: User['role'];
  }) => apiClient.post<User>(`/api/users/${id}/feishu-binding`, data),

  update: (id: number, data: {
    name?: string;
    phone?: string;
    department?: string;
    role?: User['role'];
    status?: User['status'];
  }) =>
    apiClient.put<User>(`/api/users/${id}`, data),

  delete: (id: number) => apiClient.delete<void>(`/api/users/${id}`)
};

export const feishuDirectoryAPI = {
  getStatus: () =>
    apiClient.get<FeishuDirectorySyncState>('/api/admin/feishu-directory/status'),

  sync: () =>
    apiClient.post<FeishuDirectorySyncState>('/api/admin/feishu-directory/sync'),

  getCandidates: (params?: { search?: string; active_only?: boolean }) =>
    apiClient.get<FeishuDirectoryCandidate[]>(
      '/api/admin/feishu-directory/candidates',
      { params }
    )
};

// 活动记录 API
export const activitiesAPI = {
  getAll: (params?: {
    page?: number;
    page_size?: number;
    activity_type?: Activity['activity_type'];
    start_date?: string;
    end_date?: string;
    search?: string;
    documents_only?: boolean;
  }) =>
    apiClient.get<PaginatedResponse<Activity>>('/api/activities', { params })
};

// 统计 API
export const statsAPI = {
  getDashboard: () =>
    apiClient.get<DashboardStats>('/api/stats/dashboard'),

  getBorrowStats: () =>
    apiClient.get<BorrowStats>('/api/stats/borrow')
};

// AI 对话 API
export const aiAPI = {
  // AI 对话
  chat: (message: string) => {
    return apiClient.post<{ response: string }>('/api/samples/chat', null, {
      params: { message }
    });
  },

  // 根据描述搜索样品
  searchByDescription: (query: string) => {
    return apiClient.post<{ samples: Sample[] }>('/api/samples/search-by-description', null, {
      params: { query }
    });
  },

  // 获取管理建议
  getManagementAdvice: () => {
    return apiClient.get<{ advice: string }>('/api/samples/management-advice');
  }
};

export default apiClient;

// 仓库盘点 API
export const stocktakesAPI = {
  getAll: (params?: { location_id?: number; page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<StocktakeRecord>>('/api/stocktakes', { params }),
  getById: (id: number) => apiClient.get<StocktakeDetail>(`/api/stocktakes/${id}`),
  create: (data: { location_id: number }) => apiClient.post<StocktakeDetail>('/api/stocktakes', data),
  scan: (id: number, data: { rfids: string[] }) => apiClient.post<StocktakeDetail>(`/api/stocktakes/${id}/scans`, data),
  verify: (id: number, itemId: number, data: { decision: 'found' | 'missing' }) =>
    apiClient.post<StocktakeDetail>(`/api/stocktakes/${id}/items/${itemId}/verify`, data),
  refresh: (id: number) => apiClient.post<StocktakeDetail>(`/api/stocktakes/${id}/refresh`),
  complete: (id: number, data: { revision: number; confirm_loss: true }) =>
    apiClient.post<StocktakeDetail>(`/api/stocktakes/${id}/complete`, data)
};
